import {
  Outlet,
  createRootRoute,
  createRoute,
  createRouter,
  lazyRouteComponent,
  redirect,
} from '@tanstack/react-router';
import { Flex, Spinner } from '@chakra-ui/react';
import { AppShell } from '@/widgets/app-shell/AppShell';
import { ClientShell } from '@/widgets/client-shell/ClientShell';
import { LoginPage } from '@/pages/LoginPage';
import { useAuthStore } from '@/shared/lib/auth/store';

/* Protected pages are code-split: each loads its own chunk on first
   navigation (preloaded on link hover via `defaultPreload: 'intent'`),
   so the initial bundle carries only the shell + login. The shell and
   login stay eager — they're needed for the very first paint. */

const DashboardPage = lazyRouteComponent(() => import('@/pages/DashboardPage'), 'DashboardPage');
const OrdersPage = lazyRouteComponent(() => import('@/pages/OrdersPage'), 'OrdersPage');
const OrderDetailsPage = lazyRouteComponent(
  () => import('@/pages/OrderDetailsPage'),
  'OrderDetailsPage',
);
const MapPage = lazyRouteComponent(() => import('@/pages/MapPage'), 'MapPage');
const OperatorsPage = lazyRouteComponent(() => import('@/pages/OperatorsPage'), 'OperatorsPage');
const ProvidersPage = lazyRouteComponent(() => import('@/pages/ProvidersPage'), 'ProvidersPage');
const ClientsPage = lazyRouteComponent(() => import('@/pages/ClientsPage'), 'ClientsPage');
const CatalogPage = lazyRouteComponent(() => import('@/pages/CatalogPage'), 'CatalogPage');
const RulesPage = lazyRouteComponent(() => import('@/pages/RulesPage'), 'RulesPage');
const PricingPage = lazyRouteComponent(() => import('@/pages/PricingPage'), 'PricingPage');
const ChatPage = lazyRouteComponent(() => import('@/pages/ChatPage'), 'ChatPage');
const RefillRequestsPage = lazyRouteComponent(
  () => import('@/pages/RefillRequestsPage'),
  'RefillRequestsPage',
);
const ReportsPage = lazyRouteComponent(() => import('@/pages/ReportsPage'), 'ReportsPage');
const ClientCabinetPage = lazyRouteComponent(
  () => import('@/pages/ClientCabinetPage'),
  'ClientCabinetPage',
);

function PendingFallback() {
  return (
    <Flex align="center" justify="center" p="10" color="fg.muted">
      <Spinner />
    </Flex>
  );
}

/* Code-based routes (no generated tree). All real pages live behind
   AppShell at /; /login is public and redirects authenticated users
   away from itself. Route-level redirects read the auth store
   directly — Zustand is sync, so this happens during navigation
   without a render flicker. */

const rootRoute = createRootRoute({
  component: () => <Outlet />,
});

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  beforeLoad: () => {
    const { status, user } = useAuthStore.getState();
    if (status === 'authenticated') {
      throw redirect({ to: user?.role === 'PROVIDER' ? '/client' : '/' });
    }
  },
  component: LoginPage,
});

const protectedRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: '_protected',
  beforeLoad: () => {
    const { status, user } = useAuthStore.getState();
    if (status === 'anonymous') throw redirect({ to: '/login' });
    // A provider account belongs in the client cabinet, not the admin shell.
    if (user?.role === 'PROVIDER') throw redirect({ to: '/client' });
  },
  component: AppShell,
});

const clientRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: '_client',
  beforeLoad: () => {
    const { status, user } = useAuthStore.getState();
    if (status === 'anonymous') throw redirect({ to: '/login' });
    // The cabinet is provider-only; anyone else bounces to the admin app.
    if (user?.role !== 'PROVIDER') throw redirect({ to: '/' });
  },
  component: ClientShell,
});

const dashboardRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/',
  component: DashboardPage,
});

const ordersRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/orders',
  component: OrdersPage,
});

const orderDetailsRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/orders/$orderId',
  component: OrderDetailsPage,
});

const mapRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/map',
  component: MapPage,
});

const operatorsRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/operators',
  component: OperatorsPage,
});

const providersRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/providers',
  component: ProvidersPage,
});

const clientsRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/clients',
  component: ClientsPage,
});

const catalogRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/catalog',
  component: CatalogPage,
});

const rulesRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/rules',
  component: RulesPage,
});

const pricingRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/pricing',
  component: PricingPage,
});

const chatRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/chat',
  component: ChatPage,
});

const refillRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/refill-requests',
  component: RefillRequestsPage,
});

const reportsRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/reports',
  component: ReportsPage,
});

const clientCabinetRoute = createRoute({
  getParentRoute: () => clientRoute,
  path: '/client',
  component: ClientCabinetPage,
});

const routeTree = rootRoute.addChildren([
  loginRoute,
  protectedRoute.addChildren([
    dashboardRoute,
    ordersRoute,
    orderDetailsRoute,
    mapRoute,
    operatorsRoute,
    providersRoute,
    clientsRoute,
    catalogRoute,
    rulesRoute,
    pricingRoute,
    chatRoute,
    refillRoute,
    reportsRoute,
  ]),
  clientRoute.addChildren([clientCabinetRoute]),
]);

export const router = createRouter({
  routeTree,
  defaultPreload: 'intent',
  defaultPendingComponent: PendingFallback,
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
